import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { finalize, map, tap } from 'rxjs/operators';

import { IAPIListRequestOptions } from '@sondermind/utilities/models-http';
import { ILocationApiResponse, Location } from '@sondermind/utilities/models-location';

import { LocationHttpService } from '@sondermind/data-access/location';

/**
 * Store that caches locations per provider (by personaProviderId). Lazy-loaded.
 * Used by clients to store the locations of all providers associated with them.
 */
@Injectable({
  providedIn: 'root'
})
export class ProviderLocationsStore {
  private readonly cacheSubject = new BehaviorSubject<Map<number, Location[]>>(new Map());
  private readonly isLoadingSubject = new BehaviorSubject<boolean>(false);
  private loadingRefCount = 0;

  readonly providerLocations$ = this.cacheSubject.asObservable();
  readonly isLoading$ = this.isLoadingSubject.asObservable();

  constructor(private readonly locationHttpService: LocationHttpService) {}

  /**
   * Returns locations for a provider. Uses cache when available; otherwise
   * fetches, caches, and emits (lazy-loaded).
   * @param providerId The provider's persona provider ID
   * @param forceRefresh When true, bypasses cache and refetches
   * @param opts Optional list request options
   * @returns Observable of Location array for the provider
   */
  getProviderLocations$(
    providerId: number,
    forceRefresh = false,
    opts: Partial<IAPIListRequestOptions> = {}
  ): Observable<Location[]> {
    const cached = this.cacheSubject.value.get(providerId);

    if (!forceRefresh && cached !== undefined) {
      return of(cached);
    }

    this.setLoading(true);

    return this.locationHttpService.loadProviderLocations$(providerId, opts).pipe(
      map((resp) => resp.data.map((item: ILocationApiResponse) => new Location(item))),
      tap((locations) => {
        const nextCache = new Map(this.cacheSubject.value);
        nextCache.set(providerId, locations);
        this.cacheSubject.next(nextCache);
      }),
      finalize(() => this.setLoading(false))
    );
  }

  private setLoading(active: boolean): void {
    this.loadingRefCount = active ? this.loadingRefCount + 1 : Math.max(0, this.loadingRefCount - 1);
    this.isLoadingSubject.next(this.loadingRefCount > 0);
  }
}
